<template>
  <Dialog :commonDialog="paymentDialog" :dialogWidth="dialogWidth">
    <template v-slot:title>
      Update Payment
      <span v-if="selectedPayment.barcode" class="ml-2 font-weight-400">
        for {{ selectedPayment.barcode }}
      </span>
    </template>

    <template v-slot:body>
      <v-container fluid>
        <v-row class="mb-3">
          <v-col md="3" class="py-1">
            <span class="count-style">Payable Amount : </span>
            <span class="count-style" style="color: orange !important">
              {{ formatMoney(invoiceTotal) }}
            </span>
          </v-col>
          <v-col md="3" class="py-1">
            <span class="count-style">Paid Amount : </span>
            <span class="count-style" style="color: green !important">
              {{ formatMoney(invoicePaid) }}
            </span>
          </v-col>
          <v-col md="3" class="py-1">
            <span class="count-style">Refund Amount : </span>
            <span class="count-style" style="color: blue !important">
              {{ formatMoney(invoiceRefund) }}
            </span>
          </v-col>
          <v-col md="3" class="py-1">
            <span class="count-style">Balance Amount : </span>
            <span class="count-style" style="color: red !important">
              {{ formatMoney(invoiceBalance) }}
            </span>
          </v-col>
        </v-row>
        <br />

        <v-form
          ref="editPaymentForm"
          v-model.trim="formValid"
          lazy-validation
          v-on:submit.stop.prevent="updatePayment()"
        >
          <v-row>
            <v-col md="6">
              <v-layout>
                <v-flex md4>
                  <label for="edit-amount" class="required"
                    >Amount Received</label
                  >
                </v-flex>
                <v-flex md8>
                  <v-text-field
                    id="edit-amount"
                    dense
                    color="cyan"
                    filled
                    type="number"
                    min="0"
                    :disabled="editLoading"
                    v-model.trim="selectedPayment.amount"
                    label="Amount Received"
                    :rules="[
                      validateRules.required(
                        selectedPayment.amount,
                        'Amount Received'
                      ),
                    ]"
                    solo
                    flat
                    hide-details
                    v-on:keypress="limitDecimal($event, selectedPayment.amount)"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-col>

            <v-col md="6">
              <v-layout>
                <v-flex md4>
                  <label for="edit-payment-mode" class="required"
                    >Payment Mode</label
                  >
                </v-flex>
                <v-flex md8>
                  <v-select
                    id="edit-payment-mode"
                    :items="paymentModeList"
                    dense
                    filled
                    solo
                    flat
                    hide-details
                    :rules="[
                      validateRules.required(
                        selectedPayment.payment_mode,
                        'Payment Mode'
                      ),
                    ]"
                    :disabled="editLoading"
                    v-model.trim="selectedPayment.payment_mode"
                    append-outer-icon="mdi-cog"
                    v-on:click:append-outer="managePaymentModeDialog = true"
                    label="Payment Mode"
                    color="cyan"
                    item-text="text"
                    item-value="id"
                    item-color="cyan"
                    :menu-props="{
                      offsetY: true,
                      bottom: true,
                    }"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-col>

            <v-col md="6">
              <v-layout>
                <v-flex md4>
                  <label for="edit-payment-date">Payment Date</label>
                </v-flex>
                <v-flex md8>
                  <DatePicker
                    solo
                    id="edit-payment-date"
                    :pageLoading="editLoading"
                    placeholder="Payment Date"
                    :value="selectedPayment.payment_date"
                    v-on:update:date-picker="setPaymentDate"
                  ></DatePicker>
                </v-flex>
              </v-layout>
            </v-col>

            <v-col md="6">
              <v-layout>
                <v-flex md4>
                  <label for="edit-transaction-id"
                    >Bank / Order Transaction ID</label
                  >
                </v-flex>
                <v-flex md8>
                  <v-text-field
                    id="edit-transaction-id"
                    dense
                    color="cyan"
                    filled
                    :disabled="editLoading"
                    v-model.trim="selectedPayment.transaction_id"
                    label="Bank / Order Transaction ID"
                    :rules="[
                      validateRules.minLength(
                        selectedPayment.transaction_id,
                        'Transaction ID',
                        1
                      ),
                      validateRules.maxLength(
                        selectedPayment.transaction_id,
                        'Transaction ID',
                        100
                      ),
                    ]"
                    solo
                    flat
                    hide-details
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-col>
            <v-col md="6">
              <v-layout>
                <v-flex md4>
                  <label for="payment-type">Payment Method</label>
                </v-flex>
                <v-flex md8>
                  <v-select
                    id="payment-type"
                    :items="paymentTypeList"
                    dense
                    filled
                    solo
                    flat
                    hide-details
                    :disabled="editLoading"
                    v-model.trim="selectedPayment.payment_type"
                    label="Payment Method"
                    color="cyan"
                    item-text="text"
                    item-value="id"
                    item-color="cyan"
                    :menu-props="{
                      offsetY: true,
                      bottom: true,
                    }"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-col>

            <v-col md="12">
              <label for="approval-code"> CC No / Trace / Approval Code </label>

              <TextAreaField
                id="approval-code"
                auto-grow
                dense
                filled
                placeholder="CC No / Trace / Approval Code"
                color="cyan"
                v-model="selectedPayment.approval_code"
                solo
                flat
                row-height="25"
                counter="200"
                maxlength="200"
              />
            </v-col>

            <v-col md="12">
              <label>Leave a note</label>
              <TextAreaField
                auto-grow
                dense
                filled
                placeholder="Leave a note"
                color="cyan"
                v-model="selectedPayment.admin_note"
                solo
                flat
                row-height="25"
                counter="250"
              />
            </v-col>

            <v-col md="12">
              <label for="attachments">Attachments</label>
              <FileTemplate
                id="attachments"
                v-on:file:updated="updateFiles"
                allowUpload
              ></FileTemplate>
            </v-col>
          </v-row>
        </v-form>

        <ManagePaymentMode
          :commonDialog="managePaymentModeDialog"
          :paymentMode="paymentModeList"
          v-on:close-dialog="managePaymentModeDialog = false"
          v-on:get-payment-mode="getPaymentModes()"
        ></ManagePaymentMode>
      </v-container>
    </template>

    <template v-slot:action>
      <v-btn
        v-on:click="$emit('close')"
        :disabled="editLoading"
        class="mx-2 custom-grey-border custom-bold-button"
      >
        Cancel
      </v-btn>
      <v-btn
        :disabled="!formValid || editLoading"
        :loading="editLoading"
        class="mx-2 custom-bold-button white--text"
        color="cyan"
        v-on:click="updatePayment()"
      >
        Save Payment
      </v-btn>
    </template>
  </Dialog>
</template>

<style scoped>
.count-style {
  font-weight: 600 !important;
  font-size: 18px !important;
}
</style>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import ValidationMixin from "@/core/plugins/validation-mixin";
import Dialog from "@/view/pages/partials/Dialog.vue";
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import ManagePaymentMode from "@/view/pages/partials/Manage-Payment-Mode.vue";
import TextAreaField from "@/view/components/app-component/TextAreaField.vue";
import FileTemplate from "@/view/pages/partials/FileTemplate.vue";
import { QUERY, PATCH } from "@/core/services/store/request.module";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";

export default {
  mixins: [CommonMixin, ValidationMixin],
  props: {
    paymentDialog: {
      type: Boolean,
      default: false,
    },
    paymentId: {
      type: Number,
      default: null,
    },
    detail: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      editLoading: false,
      formValid: true,
      managePaymentModeDialog: false,
      paymentModeList: [],
      selectedPayment: {
        id: null,
        barcode: null,
        amount: null,
        payment_mode: null,
        payment_date: null,
        transaction_id: null,
        admin_note: null,
        status: null,
        invoice_total: 0,
        invoice_paid: 0,
        invoice_refund: 0,
        invoice_balance: 0,
        payment_type: null,
        approval_code: null,
        documents: [],
      },
      paymentTypeList: [
        { id: "deposit", text: "Deposit Payment" },
        { id: "balance", text: "Balance Payment" },
        { id: "full", text: "Full Payment" },
      ],
    };
  },
  watch: {
    paymentId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchPayment();
        }
      },
    },
  },
  methods: {
    setPaymentDate(param) {
      this.selectedPayment.payment_date = param;
    },
    updateFiles(param) {
      this.selectedPayment.documents = param;
    },

    fetchPayment() {
      this.editLoading = true;
      this.$store
        .dispatch(QUERY, {
          url: `payment/options/${this.paymentId}`,
          data: { attribute: ["payment", "payment_modes"] },
        })
        .then(({ data }) => {
          const result = data.data || data;
          this.selectedPayment = {
            id: result.payment.id,
            barcode: result.payment.barcode || null,
            amount: result.payment.amount,
            payment_mode: this.lodash.toSafeInteger(
              result.payment.payment_mode
            ),
            payment_date: result.payment.payment_date || null,
            transaction_id: result.payment.transaction_id || null,
            admin_note: result.payment.admin_note || null,
            status: this.lodash.toSafeInteger(result.payment.status),
            approval_code: result.payment.approval_code || null,
            payment_type: result.payment.payment_type || null,
            documents: result.payment.documents || [],
          };
          this.paymentModeList = result.payment_modes || [];
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.editLoading = false;
        });
    },

    getPaymentModes() {
      this.$store
        .dispatch(QUERY, {
          url: "payment/options",
          data: { attribute: ["payment_modes"] },
        })
        .then(({ data }) => {
          if (data && data.payment_modes) {
            this.paymentModeList = data.payment_modes;
          }
        })
        .catch((error) => {
          this.logError(error);
        });
    },

    updatePayment() {
      const formErrors = this.validateForm(this.$refs.editPaymentForm);
      if (formErrors.length) {
        for (let i = 0; i < formErrors.length; i++) {
          ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
        }
        return false;
      }

      if (!this.selectedPayment.amount || this.selectedPayment.amount == 0) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please enter an amount greater than zero.")
        );
        return false;
      }

      if (this.editLoading) return false;
      this.editLoading = true;

      const formData = {
        amount: this.lodash.toNumber(this.selectedPayment.amount) || 0.0,
        payment_mode:
          this.lodash.toSafeInteger(this.selectedPayment.payment_mode) || 0,
        payment_date: this.selectedPayment.payment_date || null,
        transaction_id: this.selectedPayment.transaction_id || null,
        admin_note: this.selectedPayment.admin_note || null,
        status: this.selectedPayment.status,
        approval_code: this.selectedPayment.approval_code,
        payment_type: this.selectedPayment.payment_type || null,
        documents: this.selectedPayment.documents || [],
      };

      this.$store
        .dispatch(PATCH, {
          url: `payment/${this.selectedPayment.id}`,
          data: formData,
        })
        .then(() => {
          this.$emit("success", true);
          this.$emit("close", true);
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.editLoading = false;
        });
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 60);
    },
    invoiceTotal() {
      return Number(this.detail?.total || 0);
    },
    invoicePaid() {
      return Number(this.detail?.paid_amount || 0);
    },
    invoiceRefund() {
      return Number(this.detail?.refund_amount || 0);
    },
    invoiceBalance() {
      return (
        Number(this.detail?.total || 0) -
        Number(this.detail?.paid_amount || 0) +
        Number(this.detail?.refund_amount || 0)
      );
    },
  },
  components: {
    Dialog,
    DatePicker,
    ManagePaymentMode,
    FileTemplate,
    TextAreaField,
  },
};
</script>
